"use client"

import { useCallback, useEffect, useRef, useState } from "react"

import { MuxVideoSlot } from "@/components/marketing/mux-video-slot"
import { cn } from "@/lib/utils"

// THE FLOATING VSL, the six offer pages' master video.
//
// It AUTOPLAYS MUTED the first time the visitor scrolls it into view, with a
// clear sound-off marker asking him to tap for sound. Once playing - by
// autoplay or by a real tap - scrolling past shrinks it to a corner card that
// KEEPS PLAYING - same audio state, same position in the video, no restart -
// until he scrolls back to its slot or dismisses it. This is a page FEATURE,
// not a module: the six offer pages render this component directly with their
// lane's master from web/lib/mux-player-config.ts (COACHING_VSL or DFY_VSL).
//
// The mechanism is CSS, not DOM surgery. The module's demo moved the <video>
// node between two containers with appendChild, which a browser is free to pause
// mid-move; in React it would also unmount and remount the player, which
// restarts playback outright. Here the player NEVER moves in the tree: only the
// class on its wrapper changes, from "absolute inset-0" inside the docked
// placeholder to "fixed" in the corner. One node, one player, uninterrupted
// playback, and the media events still reach the listeners below because the
// fixed card is still a DOM descendant of the dock.
//
// The landing page deliberately does NOT use this: it carries both masters side
// by side, and two videos cannot float into the same corner.
type Vsl = {
  playbackId: string
  videoId: string
  videoTitle: string
}

export function FloatingVsl({ vsl, className }: { vsl: Vsl; className?: string }) {
  const dockRef = useRef<HTMLDivElement | null>(null)
  const cardRef = useRef<HTMLDivElement | null>(null)
  // Started, not merely mounted: a video that never began playing must not
  // follow him down the page. A PAUSED video keeps floating (the standard
  // floating-player behavior - YouTube's miniplayer and
  // Wistia's float both persist while paused): pausing in the dock and
  // scrolling away used to kill the float forever. Only an ENDED video stays
  // put - there is nothing left to keep playing at him.
  const [started, setStarted] = useState(false)
  const [ended, setEnded] = useState(false)
  const [dockVisible, setDockVisible] = useState(true)
  const [dismissed, setDismissed] = useState(false)
  const [yielding, setYielding] = useState(false)
  // The muted-autoplay pair. autoStart mounts the player muted once the slot
  // is half in view; muted drives the tap-for-sound marker and is kept honest
  // by the volumechange listener below, so unmuting through the player's own
  // volume control clears the marker exactly like tapping it.
  const [autoStart, setAutoStart] = useState(false)
  const [muted, setMuted] = useState(false)
  // Refs, because the observer callback closes over the first render's state.
  const autoStartedRef = useRef(false)
  const startedRef = useRef(false)

  const floating = started && !ended && !dockVisible && !dismissed

  // Media events do not bubble, but they DO run the capture phase down from the
  // root, so a capture listener on the dock hears the player's own play/pause
  // even though the <video> itself lives inside mux-player's shadow DOM.
  useEffect(() => {
    const node = dockRef.current
    if (!node) return
    const onPlay = () => {
      startedRef.current = true
      setStarted(true)
      // Replaying a finished video makes it followable again.
      setEnded(false)
    }
    const onEnded = () => setEnded(true)
    // The shadow <video> is the event target, so its live muted property is
    // the truth the marker follows.
    const onVolume = (e: Event) => {
      const media = e.target as Partial<HTMLMediaElement>
      if (typeof media.muted === "boolean") setMuted(media.muted)
    }
    node.addEventListener("play", onPlay, true)
    node.addEventListener("ended", onEnded, true)
    node.addEventListener("volumechange", onVolume, true)
    return () => {
      node.removeEventListener("play", onPlay, true)
      node.removeEventListener("ended", onEnded, true)
      node.removeEventListener("volumechange", onVolume, true)
    }
  }, [])

  // The dock keeps its own box whether or not it currently holds the player, so
  // this observer keeps reporting honestly while the card is floating, and the
  // page never jumps when the card leaves or returns.
  useEffect(() => {
    const node = dockRef.current
    if (!node || typeof IntersectionObserver === "undefined") return
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        if (!entry) return
        setDockVisible(entry.isIntersecting)
        // Scrolling back to the slot clears a dismissal: he is looking at the
        // video again, so the next time he scrolls away it may follow him again.
        if (entry.isIntersecting) setDismissed(false)
        // Half in view for the first time, and never played by hand: start it
        // muted. A visitor who already tapped play keeps his sound; the ref
        // guard is what stops this branch from muting that session.
        if (
          entry.intersectionRatio >= 0.5 &&
          !autoStartedRef.current &&
          !startedRef.current
        ) {
          autoStartedRef.current = true
          setAutoStart(true)
          setMuted(true)
        }
      },
      { threshold: [0, 0.5] },
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [])

  // THE CARD NEVER COVERS THE ASK. A corner card sits exactly where a centred
  // Apply button lands on a phone, and a button a thumb cannot reach is the one
  // thing on these pages that costs money. So while it floats, it watches for an
  // Apply / checkout button under itself and fades out until that button has
  // scrolled by. It keeps playing throughout - it steps aside, it does not stop.
  //
  // Every setState here is scheduled on a frame rather than run in the effect
  // body, including the reset: a synchronous one cascades a second render (see
  // web/AGENTS.md, "derive missing-input UI before an effect").
  useEffect(() => {
    let frame = 0
    if (!floating) {
      frame = window.requestAnimationFrame(() => setYielding(false))
      return () => window.cancelAnimationFrame(frame)
    }
    const asks = [
      ...document.querySelectorAll<HTMLElement>('a.cta, a[href*="/apply"], a[href*="/pay"]'),
    ]
    // Only a real ask counts: one he can see, that scrolls with the page. The
    // selector also matches things that are not that, and
    // getBoundingClientRect reports a box for each of them: the closed menu
    // drawer's links (aria-hidden, inert and opacity 0 until the menu opens),
    // the always-on Find your program bar at the foot of a phone screen
    // (fixed), the header's Find your program button (inside the sticky
    // header, pinned to the top for the whole page), and anything else hidden
    // by opacity or visibility. A pinned ask never scrolls by, so the card
    // would wait on it for the whole page; the card is sized and layered to
    // stay clear of both bars instead (see its class below). These checks run
    // only for an ask that already overlaps the card, so a scroll frame stays
    // cheap.
    const isPinned = (node: HTMLElement) => {
      const style = window.getComputedStyle(node)
      if (style.position === "fixed") return true
      if (style.position !== "sticky") return false
      // A sticky box is pinned while it sits at its offset (the site header
      // at top 0). Measured, not assumed: a sticky box inside a short section
      // does scroll away once its section ends.
      const r = node.getBoundingClientRect()
      const top = parseFloat(style.top)
      const bottom = parseFloat(style.bottom)
      return (
        (Number.isFinite(top) && Math.abs(r.top - top) < 1) ||
        (Number.isFinite(bottom) && Math.abs(window.innerHeight - r.bottom - bottom) < 1)
      )
    }
    const isRealAsk = (ask: HTMLElement) => {
      if (ask.closest('[aria-hidden="true"], [inert]')) return false
      if (
        typeof ask.checkVisibility === "function" &&
        !ask.checkVisibility({ opacityProperty: true, visibilityProperty: true })
      ) {
        return false
      }
      for (let node: HTMLElement | null = ask; node; node = node.parentElement) {
        if (isPinned(node)) return false
      }
      return true
    }
    const measure = () => {
      frame = 0
      const card = cardRef.current
      if (!card) return
      const c = card.getBoundingClientRect()
      // A little breathing room, so the card is already gone rather than just
      // touching the button by the time he reaches for it.
      const pad = 16
      const covers = asks.some((ask) => {
        const r = ask.getBoundingClientRect()
        if (r.width === 0 || r.bottom < 0 || r.top > window.innerHeight) return false
        const overlaps = !(
          r.right < c.left - pad ||
          r.left > c.right + pad ||
          r.bottom < c.top - pad ||
          r.top > c.bottom + pad
        )
        return overlaps && isRealAsk(ask)
      })
      setYielding(covers)
    }
    const onScroll = () => {
      if (frame) return
      frame = window.requestAnimationFrame(measure)
    }
    frame = window.requestAnimationFrame(measure)
    window.addEventListener("scroll", onScroll, { passive: true })
    window.addEventListener("resize", onScroll)
    return () => {
      if (frame) window.cancelAnimationFrame(frame)
      window.removeEventListener("scroll", onScroll)
      window.removeEventListener("resize", onScroll)
    }
  }, [floating])

  // Dismiss means gone, so it pauses too. A card that only hid itself would keep
  // talking from an invisible corner, which is the one thing worse than being in
  // the way.
  const dismiss = useCallback(() => {
    setDismissed(true)
    const player = dockRef.current?.querySelector("mux-player") as
      | (HTMLElement & { pause?: () => void })
      | null
    player?.pause?.()
  }, [])

  // Turning the sound on is one property write; the volumechange listener
  // above then confirms it, so the marker cannot linger after a real unmute.
  const unmute = useCallback(() => {
    const player = dockRef.current?.querySelector("mux-player") as
      | (HTMLElement & { muted?: boolean })
      | null
    if (player) player.muted = false
    setMuted(false)
  }, [])

  return (
    <div
      ref={dockRef}
      data-ca-section="floating-vsl"
      className={cn("relative mx-auto aspect-[9/16] w-full max-w-[380px]", className)}
    >
      <div
        ref={cardRef}
        className={cn(
          // On a phone the card floats above the Find your program bar, never
          // on it: the bar is 65px (mobile-sticky-cta.tsx: a 1px rule, 10px,
          // the 44px button, 10px) plus the safe area it pads for, and the
          // card keeps the same 16px gap above it that it keeps from the
          // screen edge. From sm up there is no bar and it sits 24px in.
          //
          // It also stays under the site header, both ways. Layered: z-30
          // sits below the header's z-40, so the open phone menu (part of the
          // header) always paints over the card and takes its own taps. Sized:
          // on a short screen (a phone held sideways) the card would otherwise
          // reach up under the header and lose its Close button there,
          // so its width, which sets its 9:16 height, is capped at what fits
          // between the header and its own bottom offset. --vsl-cap budgets
          // the 65px header, a 16px gap under it, the ~6px the card measures
          // beyond 9:16 (its border and the player's line box) and the bottom
          // offset: 167px below sm, 110px from sm. The usual 40vw between
          // 132px and 200px applies whenever it fits.
          floating
            ? "fixed bottom-[calc(81px_+_env(safe-area-inset-bottom))] right-4 z-30 w-[clamp(min(132px,var(--vsl-cap)),40vw,min(200px,var(--vsl-cap)))] drop-shadow-2xl transition-opacity duration-200 [--vsl-cap:calc((100dvh_-_167px_-_env(safe-area-inset-bottom))*9/16)] motion-safe:animate-[ca-float-in_.4s_cubic-bezier(.22,.61,.36,1)] sm:bottom-6 sm:right-6 sm:[--vsl-cap:calc((100dvh_-_110px)*9/16)]"
            : "absolute inset-0",
          // Yielding, not stopping: it keeps talking while it steps out of the
          // way of an Apply button, then fades straight back. Read only while
          // floating, so a stale value can never blank the docked player for a
          // frame on the way back in.
          floating && yielding && "pointer-events-none opacity-0",
        )}
      >
        <MuxVideoSlot
          aspect="portrait"
          playbackId={vsl.playbackId}
          videoId={vsl.videoId}
          videoTitle={vsl.videoTitle}
          className="max-w-none"
          autoActivate={autoStart}
          // The tap on the poster is the one signal that cannot be missed: it is
          // our own button, not a media event crossing a shadow boundary. The
          // capture listeners above are what keep the state honest afterwards.
          onActivate={() => {
            startedRef.current = true
            setStarted(true)
          }}
        />
        {started && muted ? (
          // data-player-state: this control exists only once the muted
          // autoplay has started, which depends on timing, so
          // check:module-type leaves it out of a band's measured typography.
          <button
            type="button"
            data-player-state
            onClick={unmute}
            // The after: box makes the tap area 44px tall with nothing on
            // screen changing. It reaches down over the video, never up, so it
            // stays clear of the Close button's corner on the floating card.
            className={cn(
              "absolute left-3 top-3 z-10 flex items-center gap-2 rounded-full border border-white/25 bg-black/80 px-3.5 py-2 font-semibold text-white transition-colors after:absolute after:inset-x-0 after:top-0 after:h-11 after:content-[''] hover:bg-black",
              floating ? "left-2 top-2 px-2.5 py-1.5 text-[11px]" : "text-[13px]",
            )}
          >
            <svg viewBox="0 0 24 24" className="h-4 w-4 shrink-0" fill="currentColor" aria-hidden>
              <path d="M3 9v6h4l5 4V5L7 9H3z" />
              <path
                d="M16 9l5 6M21 9l-5 6"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                fill="none"
              />
            </svg>
            {/* The corner card is ~180px wide and carries the dismiss X in its
                top-right corner, so the full sentence wraps into it. */}
            {floating ? "Tap to unmute" : "Sound is off. Tap to unmute."}
          </button>
        ) : null}
        {floating ? (
          <button
            type="button"
            data-player-state
            onClick={dismiss}
            aria-label="Close the video"
            // Close is the only way to stop a floating video, so its tap area
            // is 44px (the 28px circle plus an invisible 8px ring, the
            // intro-course-popup close's pattern): a near miss closes the
            // card instead of landing on the player and toggling playback.
            className="absolute -right-2 -top-2 flex h-7 w-7 items-center justify-center rounded-full border border-white/25 bg-black/80 text-white transition-colors after:absolute after:-inset-2 after:content-[''] hover:bg-black"
          >
            <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" aria-hidden>
              <path
                d="M6 6l12 12M18 6L6 18"
                stroke="currentColor"
                strokeWidth="2.4"
                strokeLinecap="round"
              />
            </svg>
          </button>
        ) : null}
      </div>
    </div>
  )
}
